import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MaterialModule } from '../../shared/material.module';
import { ListingService} from 'src/app/services/listing.service';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { AIContentService, AIContentRequest, AIContentResponse, AIDescriptionResponse } from 'src/app/services/ai-content.service';
import { CardsService } from 'src/app/services/cards.service';
import { AuthService } from 'src/app/services/auth.service';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { AiChatSelectionService, AISelectionData } from 'src/app/services/ai-selection.service'
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';



type ListingType = 'product' | 'service' | 'events' | 'charity' | 'reviews' | 'projects';
type AiSupportedType = 'product' | 'service';

export type ProjectStatus =
  | "upcoming"
  | "on progress"
  | "completed"
  | "paused"
  | "archived";

@Component({
  selector: 'app-listing-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, FormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule,BreadcrumbComponent],

  templateUrl: './listing-form.component.html',
  styleUrls: ['./listing-form.component.scss']
})
export class ListingFormComponent implements OnInit, OnDestroy, OnChanges {
  // Inputs from parent (ViewCard)
  @Input() initialType?: ListingType;
  @Input() cardId?: string;
  @Input() preloadedListings?: any; // ✅ Accept data from ViewCard
  

  // --- Type metadata & UI ---
  listingTypes: ListingType[] = ['product', 'service', 'events', 'charity', 'reviews', 'projects'];
  typeMeta: Record<ListingType, { label: string; icon: string; singular: string }> = {
    product: { label: 'Products', icon: 'inventory', singular: 'Product' },
    service: { label: 'Services', icon: 'room_service', singular: 'Service' },
    events: { label: 'Events', icon: 'event', singular: 'Event' },
    charity: { label: 'Charity', icon: 'volunteer_activism', singular: 'Charity' },
    reviews: { label: 'Reviews', icon: 'rate_review', singular: 'Review' },
    projects: { label: 'Projects', icon: 'work', singular: 'Project' }
  };
  statusOptions: ProjectStatus[] = [
    'upcoming',
    'on progress',
    'completed',
    'paused',
    'archived'
  ];

  // AI Chat UI state
showAIChatModal: boolean = false;                 // toggle modal visibility
aiChatInitialMessage: string = '';               // prefill message for AI chat
aiChatSelection$?: Observable<{ name: string; description: string }>; // emits selected suggestion


  activeType: ListingType = 'product';
  // map type -> observable from listingService
  private typeObservables!: Record<ListingType, Observable<any[]>>;
  activeItems$!: Observable<any[]>;

  // Expose loading + error (initialized in ngOnInit to avoid "used before init" issues)
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;

  // generic reactive form (fields shown/hidden by template)
  listingForm: FormGroup;
 selectedValue: string = '';
  // UI state
  viewMode: 'grid' | 'list' = 'grid';
  showCreateForm = false;
  creating = false;
  editingItem: any | null = null;

  // images/files
  uploadedFiles: File[] = [];
  uploadProgress: number[] = [];
  imagePreviews: string[] = [];
  existingImages: string[] = [];
  imagesToDelete: string[] = [];
  private skipImageReset = false; 

  // image modal
  showImageModal = false;
  modalImages: string[] = [];
  selectedItemForModal: any | null = null;
  selectedImageIndex = 0;

  // AI suggestions
  showSuggestionsModal = false;
  suggestions: { name: string; description: string }[] = [];
  selectedSuggestion: { name: string; description: string } | null = null;
  generatingFullContent = false;
  generatingAIContent = false;
  aiError = '';
  expandedItems: Set<string> = new Set();
  expandedDescriptions: Set<string> = new Set();
expandedFeatures: Set<string> = new Set();
expandedImages: Set<string> = new Set();

  // Flag to prevent form reset when coming from AI chat
  private skipFormReset = false;

  // Feature/Amenity validation
  maxFeaturesAllowed = 5;
  featureErrorMessage = '';
  amenityErrorMessage = '';
  get amenitiesArray() {
    return this.listingForm.get('amenities') as any;
  }

  addAmenity(value: string = '') {
    const currentAmenitiesCount = this.amenitiesArray.length;
    if (currentAmenitiesCount >= this.maxFeaturesAllowed) {
      this.amenityErrorMessage = `Maximum ${this.maxFeaturesAllowed} amenities allowed. Please remove existing amenities before adding new ones.`;
      setTimeout(() => {
        this.amenityErrorMessage = '';
      }, 5000);
      return;
    }
    this.amenityErrorMessage = '';
    this.amenitiesArray.push(this.fb.control(value));
  }

  removeAmenity(index: number) {
    this.amenitiesArray.removeAt(index);
    this.amenityErrorMessage = '';
  }

  private destroy$ = new Subject<void>();
  

  constructor(
    private fb: FormBuilder,
    private listingService: ListingService,
    private storageService: FirebaseStorageService,
    private aiContentService: AIContentService,
    private cardsService: CardsService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private selectionService: AiChatSelectionService
  ) {
    // Generic form with fields covering all listing types.
    this.listingForm = this.fb.group({
      nameOrTitle: ['', Validators.required],
      description: [''],
      price: [0],
      currency: ['INR'],
      features: this.fb.array([]),
      amenities: this.fb.array([]),
      projectType: [''],
      status: [''],
      youtubeId: [''],
      review: [''],
      reviewerName: ['']
    });

    // wire type -> observable (safe to do in constructor because listingService is injected)
    this.typeObservables = {
      product: this.listingService.products$,
      service: this.listingService.services$,
      events: this.listingService.events$,
      charity: this.listingService.charity$,
      reviews: this.listingService.reviews$,
      projects: this.listingService.projects$ // Add this observable in your service
    };

    // Do not finalize activeItems$ here — ngOnInit / ngOnChanges will set it to honor inputs
    this.activeItems$ = this.typeObservables[this.activeType];
  }

  // current card id (from your CardsService)
  get currentCardId(): string | null {
    return this.cardsService.selectedCard()?.id ?? null;
  }
  onSelectChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedValue = selectElement.value;
    console.log('Selected value:', this.selectedValue);
  }

 ngOnInit(): void {
    this.loading$ = this.listingService.loading$;
    this.error$ = this.listingService.error$;

    // Handle query parameters for editing
    const routeParams = this.route.snapshot.queryParamMap;
    const itemId = routeParams.get('itemId');
    const type = routeParams.get('type') as ListingType;

    // Set active type from query parameter
    if (type && this.listingTypes.includes(type)) {
      this.activeType = type;
      console.log('🔄 Setting active type from query param:', type);
    }

    this.activeItems$ = this.typeObservables[this.activeType];

    // Check for AI selection data
    this.checkForStoredAISelection();

    this.selectionService.selection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(suggestion => {
        console.log('Received AI selection in listing form component:', suggestion);
        this.applyAISuggestionFromChat(suggestion);
      });

    // Load data
    this.loadListingData();

    // If there's an itemId, load that item for editing
    if (itemId) {
      this.loadItemForEditing(itemId);
    }
  }

  private loadItemForEditing(itemId: string): void {
    this.activeItems$.pipe(takeUntil(this.destroy$)).subscribe(items => {
      const item = items.find(i => i.id === itemId);
      if (item) {
        this.editingItem = item;
        this.populateFormForEditing(item);
      }
    });
  }
private populateFormForEditing(item: any): void {
    console.log('🔧 Populating form for editing item:', item);
    
    const title = item?.name ?? item?.title ?? item?.review ?? '';
    this.listingForm.patchValue({
      nameOrTitle: item?.title,
      description: item?.description ?? item?.review ?? '',
      price: item?.price ? Number(item.price) : 0,
      currency: item?.currency ?? 'INR',
      projectType: item?.projectType ?? '',
      status: item?.status ?? '',
      youtubeId: item?.youtubeId ?? '',
      review: item?.review ?? '',
      reviewerName: item?.name ?? ''
    });

    // Populate features array
    const featuresArray = this.listingForm.get('features') as FormArray;
    featuresArray.clear();
    if (Array.isArray(item?.features)) {
      item.features.forEach((feature: string) => featuresArray.push(this.fb.control(feature)));
    }

    // Populate amenities array for projects
    const amenitiesArray = this.listingForm.get('amenities') as any;
    amenitiesArray.clear();
    if (Array.isArray(item?.amenities)) {
      item.amenities.forEach((amenity: string) => amenitiesArray.push(this.fb.control(amenity)));
    }

    // Load existing images
    this.loadExistingImages(item);
  }

  private loadListingData(): void {
    if (this.preloadedListings) {
      console.log('[ListingFormComponent] Using preloaded data');
      this.listingService.listingsSubject.next(this.preloadedListings);
    } else {
      const id = this.cardId ?? this.currentCardId;
      if (id) {
        this.listingService.fetchListings(id);
      }
    }
  }

  // FIX THE CANCEL BUTTON
 goBack(): void {
  const panel = this.activeType === 'product' ? 'products' :
                this.activeType === 'service' ? 'services' :
                this.activeType === 'events' ? 'events' :
                this.activeType === 'charity' ? 'charity' :
                this.activeType === 'reviews' ? 'reviews' :
                this.activeType === 'projects' ? 'projects' : '';

  this.router.navigate(['/view', this.cardId ?? this.currentCardId], {
    queryParams: {
      panel,
      openForm: 'false' // or remove this param if you don't want form open
    }
  });
}

  closeCreateForm(): void {
    this.goBack();
  }

  openCreateForm(item?: any) {
    // This method isn't needed for route-based component
    console.log('🔧 openCreateForm called in route-based component');
    this.editingItem = item ?? null;
  }
 ngOnChanges(changes: SimpleChanges): void {
  // Handle type changes (no refetching)
  if (changes['initialType'] && changes['initialType'].currentValue) {
    const newType = changes['initialType'].currentValue as ListingType;
    if (this.listingTypes.includes(newType)) {
      this.selectType(newType, false); // ✅ false = don't fetch
    }
  }

  // ✅ Handle preloaded data updates
  if (changes['preloadedListings'] && changes['preloadedListings'].currentValue) {
    console.log('[ListingComponent] Received updated preloaded data');
    this.listingService.listingsSubject.next(changes['preloadedListings'].currentValue);
  }
}

get featuresArray() {
  return this.listingForm.get('features') as any;
}

addFeature(value: string = '') {
  const currentFeaturesCount = this.featuresArray.length;
  
  if (currentFeaturesCount >= this.maxFeaturesAllowed) {
    this.featureErrorMessage = `Maximum ${this.maxFeaturesAllowed} features allowed. Please remove existing features before adding new ones.`;
    console.log('🚫 Feature limit reached:', this.featureErrorMessage);
    
    // Clear the error message after 5 seconds
    setTimeout(() => {
      this.featureErrorMessage = '';
    }, 5000);
    
    return;
  }
  
  // Clear any existing error message
  this.featureErrorMessage = '';
  this.featuresArray.push(this.fb.control(value));
  console.log(`✅ Feature added. Current count: ${currentFeaturesCount + 1}/${this.maxFeaturesAllowed}`);
}

removeFeature(index: number) {
  this.featuresArray.removeAt(index);
  // Clear any error message when removing features
  this.featureErrorMessage = '';
  console.log(`✅ Feature removed. Current count: ${this.featuresArray.length}/${this.maxFeaturesAllowed}`);
}

// Helper method to check if feature limit is reached
isFeatureLimitReached(): boolean {
  return this.featuresArray.length >= this.maxFeaturesAllowed;
}

// Helper method to get remaining features count
getRemainingFeaturesCount(): number {
  return Math.max(0, this.maxFeaturesAllowed - this.featuresArray.length);
}

// Option 3: Enhanced Service Solution Methods
private checkForStoredAISelection(): void {
  console.log('� Option 3: Checking for AI selection via enhanced service...');

  // Try to consume pending AI selection
  const selectionData = this.selectionService.consumePendingSelection();
  
  if (selectionData) {
    console.log('� Option 3: Found AI selection data:', selectionData);
      
      // Check if data is recent (within last 60 seconds to be safe)
    // Switch to correct tab first if type is different
    if (selectionData.type && selectionData.type !== this.activeType) {
      console.log(`🔄 Option 3: Switching to tab: ${selectionData.type} (current: ${this.activeType})`);
      this.selectType(selectionData.type, false);
    }
    
    // Wait for tab switch, then open and populate form
    setTimeout(() => {
      console.log('🔄 Option 3: Opening create form...');
      this.skipFormReset = true; // Prevent form reset when opening
      this.skipImageReset = true;
      this.openCreateForm();
      
      // Wait for form to be ready, then populate
      setTimeout(() => {
        this.populateFormWithStoredAIData(selectionData);
      }, 250);
    }, 500);
    
  } else {
    console.log('🔄 Option 3: No AI selection data available');
  }
}

private populateFormWithStoredAIData(aiData: any): void {
  console.log('🔄 Option 3: Populating form with AI selection data:', aiData);
  
  if (!this.listingForm) {
    console.error('❌ Option 3: Listing form not available, retrying...');
    // Retry after a short delay
    setTimeout(() => {
      this.populateFormWithStoredAIData(aiData);
    }, 200);
    return;
  }

  try {
    // Populate basic fields
    this.listingForm.patchValue({
      nameOrTitle: aiData.name || '',
      description: aiData.description || '',
      price: aiData.price || 0,
      currency: aiData.currency || 'INR'
    });

    console.log('🔧 Basic fields populated, form state:', this.listingForm.value);

    // Handle features array with validation
    if (aiData.features && Array.isArray(aiData.features) && aiData.features.length > 0) {
      const featuresArray = this.listingForm.get('features') as FormArray;
      featuresArray.clear();
      
      const validFeatures = aiData.features.filter((f: string) => f && f.trim());
      const featuresToAdd = validFeatures.slice(0, this.maxFeaturesAllowed);
      const excessFeatures = validFeatures.length - this.maxFeaturesAllowed;
      
      featuresToAdd.forEach((feature: string) => {
        console.log('🔧 Adding feature:', feature);
        featuresArray.push(this.fb.control(feature.trim()));
      });
      
      console.log(`🔧 Added ${featuresToAdd.length} features`);
      
      // Show notification if there were excess features
      if (excessFeatures > 0) {
        this.featureErrorMessage = `AI suggested ${validFeatures.length} features, but only ${this.maxFeaturesAllowed} are allowed. ${excessFeatures} features were not added. You can manually add more by removing existing ones first.`;
        console.log('⚠️ Feature limit notification:', this.featureErrorMessage);
        
        // Clear the notification after 8 seconds for AI cases (longer read time)
        setTimeout(() => {
          this.featureErrorMessage = '';
        }, 8000);
      }
    }
if (aiData.images && Array.isArray(aiData.images) && aiData.images.length > 0) {
      this.existingImages = [...aiData.images];
      this.imagePreviews = [...aiData.images];
      console.log('🔧 Added AI images:', this.existingImages);
    }
    console.log('✅ Form populated successfully! Final state:', this.listingForm.value);
    
    // Add a small delay and then check if the form still has the data
    setTimeout(() => {
      console.log('🔧 Form state after 1 second:', this.listingForm.value);
    }, 1000);
  } catch (error) {
    console.error('❌ Option 3: Error populating form with AI selection data:', error);
this.skipImageReset = false;  }
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Change active type (Products / Services / ...)
   * fetch = whether to trigger listingService.fetchListings() for the card
   */
  selectType(type: ListingType, fetch = true) {
    if (!this.listingTypes.includes(type)) return;
    this.activeType = type;
    this.activeItems$ = this.typeObservables[type];

    if (fetch) {
      const id = this.cardId ?? this.currentCardId;
      if (id) this.listingService.fetchListings(id);
    }
  }
generateDescriptionOnly(): void {
  const cardId = this.cardId ?? this.currentCardId;
  if (!cardId) {
    this.aiError = 'No card selected';
    return;
  }

  // Only allow for product/service listings
  if (this.activeType !== 'product' && this.activeType !== 'service') {
    this.aiError = 'AI description generation only available for products and services';
    return;
  }

  this.generatingAIContent = true;
  this.aiError = '';

  const nameOrTitle = this.listingForm.get('nameOrTitle')?.value || 'Untitled';

  // Call the unified service method
  this.aiContentService.generateDescriptionOnly(
    cardId,
    nameOrTitle,
    this.activeType as 'product' | 'service',
    'English'
  ).pipe(takeUntil(this.destroy$))
  .subscribe({
    next: (res: AIDescriptionResponse) => {
      this.generatingAIContent = false;
      // Directly apply the generated description without affecting title
      this.listingForm.patchValue({ description: res.description });
    },
    error: (err) => {
      this.generatingAIContent = false;
      this.aiError = `AI description generation failed: ${err?.message || err}`;
    }
  });
}

  // Generic fetch trigger (explicit)
  fetchListings() {
    const id = this.cardId ?? this.currentCardId;
    if (!id) return;
    this.listingService.fetchListings(id);
  }

  // --- Helpers to read fields from any item ---
  getItemTitle(item: any): string {
    return item?.name ?? item?.title ?? item?.review ?? '';
  }

  getItemDescription(item: any): string {
    return item?.description ?? item?.review ?? '';
  }

  getItemPrice(item: any): string | number | undefined {
    return item?.price;
  }

  getItemFeatures(item: any): string[] | undefined {
    if (!item?.features) return undefined;
    return Array.isArray(item.features) ? item.features : String(item.features).split(',').map(s => s.trim()).filter(Boolean);
  }

  getItemImages(item: any): string[] {
    const maybe = item?.imageUrls ?? item?.imageUrl;
    if (!maybe) return [];
    return Array.isArray(maybe) ? maybe : [maybe];
  }

  getFirstImage(item: any): string | undefined {
    const imgs = this.getItemImages(item);
    return imgs.length ? imgs[0] : undefined;
  }

  trackById(_idx: number, item: any) {
    return item?.id ?? _idx;
  }

  // convenience method used by parent ViewCard when it wants to open create+generate
  openCreateFormAndGenerateContent(): void {
    this.openCreateForm();
    setTimeout(() => this.generateFullListingContent(), 120);
  }

  // --- Create / Edit UI flow ---
 

 

  resetForm() {
    console.log('🔧 resetForm called - clearing all form data');
    this.listingForm.reset({
      nameOrTitle: '',
      description: '',
      price: 0,
      currency: 'INR',
      features: '',
      review: '',
      reviewerName: ''
    });
    this.uploadedFiles = [];
    this.uploadProgress = [];
    this.imagePreviews = [];
    this.existingImages = [];
    this.imagesToDelete = [];
    
    // Clear feature validation messages and features array
    this.featureErrorMessage = '';
    const featuresArray = this.listingForm.get('features') as FormArray;
    featuresArray.clear();
  }

  loadExistingImages(item: any) {
    const imgs = this.getItemImages(item);
    this.existingImages = [...imgs];
    this.imagePreviews = [...imgs];
  }

  onFileChange(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;
    this.uploadedFiles = [...this.uploadedFiles, ...Array.from(files)];
    this.generatePreviews();
  }

  generatePreviews() {
    const newPreviews: string[] = [];
    this.uploadedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        this.imagePreviews = [...this.existingImages, ...newPreviews];
      };
      reader.readAsDataURL(file);
    });

    if (this.uploadedFiles.length === 0) {
      this.imagePreviews = [...this.existingImages];
    }
  }

  removeImage(index: number) {
    if (index < this.existingImages.length) {
      const removed = this.existingImages.splice(index, 1)[0];
      this.imagesToDelete.push(removed);
    } else {
      const uploadedIndex = index - this.existingImages.length;
      this.uploadedFiles.splice(uploadedIndex, 1);
    }
    this.imagePreviews.splice(index, 1);
  }
  // --- Submit create / update ---
  async onSubmit(): Promise<void> {
    console.log('Active type before navigation:', this.activeType);
    const cardId = this.cardId ?? this.currentCardId;
    if (!cardId) {
      this.aiError = 'No card selected';
      return;
    }
    if (this.listingForm.invalid) return;

    this.creating = true;
    this.aiError = '';

    try {
      let newImageUrls: string[] = [];
      if (this.uploadedFiles.length > 0) {
        this.uploadProgress = Array(this.uploadedFiles.length).fill(0);
        const uploadPromises = this.uploadedFiles.map((file, i) =>
          this.storageService.uploadFile(
            `listings/${this.activeType}/${cardId}`,
            file,
            (p) => this.uploadProgress[i] = p
          )
        );
        newImageUrls = await Promise.all(uploadPromises);
      }

      const remainingExisting = this.existingImages.filter(img => !this.imagesToDelete.includes(img));
      const finalImageUrls = [...remainingExisting, ...newImageUrls];
      console.log('📦 Final image URLs being sent to backend:', finalImageUrls);

      const formValue = this.listingForm.value;
      // Normalize features into an array
        let featuresArray: string[] | undefined;

        if (Array.isArray(formValue.features)) {
          featuresArray = formValue.features
            .map((f: string) => f.trim())
            .filter(Boolean);
        } else if (typeof formValue.features === 'string') {
          featuresArray = formValue.features
            .split(',')
            .map((f: string) => f.trim())
            .filter(Boolean);
        } else {
          featuresArray = undefined;
        }      
      if (featuresArray && featuresArray.length > 5 && ['product','service','events'].includes(this.activeType)) {
          this.aiError = `Only five feature is allowed for ${this.activeType}`;
          this.creating = false;
          return;
        }
      const payloadCommon: any = { description: formValue.description || undefined };

      if (this.editingItem) {
        let updatePayload: any = {};
        switch (this.activeType) {
          case 'product':
            updatePayload = { name: formValue.nameOrTitle, price: Number(formValue.price), currency: formValue.currency, imageUrls: finalImageUrls, features: featuresArray, ...payloadCommon };
            break;
          case 'service':
            updatePayload = { title: formValue.nameOrTitle, price: Number(formValue.price), currency: formValue.currency, imageUrls: finalImageUrls, features: featuresArray, ...payloadCommon };
            break;
          case 'events':
            updatePayload = { title: formValue.nameOrTitle, price: Number(formValue.price), imageUrls: finalImageUrls, features: featuresArray, ...payloadCommon };
            break;
          case 'charity':
            updatePayload = { title: formValue.nameOrTitle, imageUrl: finalImageUrls.length ? finalImageUrls[0] : null, imageUrls: finalImageUrls, ...payloadCommon };
            break;
          case 'reviews':
            updatePayload = { title: formValue.nameOrTitle, review: formValue.review, name: formValue.reviewerName, ...payloadCommon };
            break;
          case 'projects':
            updatePayload = {
              title: formValue.nameOrTitle,
              description: formValue.description,
              projectType: formValue.projectType,
              status: formValue.status,
              youtubeId: formValue.youtubeId,
              imageUrls: finalImageUrls,
              amenities: formValue.amenities
            };
            break;
        }

        this.listingService.updateListing(this.editingItem.id!, updatePayload, cardId)
          .pipe(takeUntil(this.destroy$), finalize(() => this.creating = false))
          .subscribe({
            next: () => {
              // Navigate to the view card page with the correct panel active
              const panel = this.activeType === 'product' ? 'products' : 
                            this.activeType === 'service' ? 'services' : 
                            this.activeType === 'events' ? 'events' : 
                            this.activeType === 'charity' ? 'charity' : 
                            this.activeType === 'reviews' ? 'reviews' : 
                            this.activeType === 'projects' ? 'projects' : '';
              
              this.router.navigate(['/view', cardId], { 
                queryParams: { 
                  panel: panel,
                  openForm: 'true'
                } 
              });
            },
            error: (err) => { console.error('Update failed', err); this.aiError = 'Failed to update item'; }
          });
      } else {
        let createPayload: any = {};
        switch (this.activeType) {
          case 'product':
            createPayload = { type: 'product', name: formValue.nameOrTitle, description: formValue.description, price: Number(formValue.price), currency: formValue.currency, imageUrls: finalImageUrls, features: featuresArray };
            break;
          case 'service':
            createPayload = { type: 'service', title: formValue.nameOrTitle, description: formValue.description, price: Number(formValue.price), currency: formValue.currency, imageUrls: finalImageUrls, features: featuresArray };
            break;
          case 'events':
            createPayload = { type: 'events', title: formValue.nameOrTitle, description: formValue.description, price: Number(formValue.price), imageUrls: finalImageUrls, features: featuresArray };
            break;
          case 'charity':
            createPayload = { type: 'charity', title: formValue.nameOrTitle, imageUrl: finalImageUrls.length ? finalImageUrls[0] : null, imageUrls: finalImageUrls };
            break;
          case 'reviews':
            createPayload = { type: 'reviews', title: formValue.nameOrTitle, review: formValue.review, name: formValue.reviewerName };
            break;
          case 'projects':
            createPayload = {
              type: 'projects',
              title: formValue.nameOrTitle,
              description: formValue.description,
              projectType: formValue.projectType,
              status: formValue.status,
              youtubeId: formValue.youtubeId,
              imageUrls: finalImageUrls,
              amenities: formValue.amenities
            };
            break;
        }

        this.listingService.createListing(cardId, createPayload)
          .pipe(takeUntil(this.destroy$), finalize(() => this.creating = false))
          .subscribe({
            next: () => {
      // Navigate to the view card page with the correct panel active
      const panel = this.activeType === 'product' ? 'products' : 
                    this.activeType === 'service' ? 'services' : 
                    this.activeType === 'events' ? 'events' : 
                    this.activeType === 'charity' ? 'charity' : 
                    this.activeType === 'reviews' ? 'reviews' : 
                    this.activeType === 'projects' ? 'projects' : '';
      
      this.router.navigate(['/view', cardId], { 
        queryParams: { 
          panel: panel,
          openForm: 'true'
        } 
      });
    },
            error: (err) => { console.error('Create failed', err); this.aiError = 'Failed to create item'; }
          });
      }
    } catch (err) {
      console.error('Upload or save failed', err);
      this.aiError = 'Failed to upload images or save item';
      this.creating = false;
    }
  }

  // Delete
  deleteItem(itemId: string) {
    if (!confirm('Are you sure you want to delete this item?')) return;
    const cardId = this.cardId ?? this.currentCardId ?? undefined;
    this.listingService.deleteListing(itemId, cardId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: (err) => { console.error('Delete failed', err); this.aiError = 'Failed to delete item'; }
      });
  }

  // --- Image modal helpers ---
  openImageGallery(item: any, startIndex = 0) {
    const imgs = this.getItemImages(item);
    if (!imgs || imgs.length === 0) return;
    this.modalImages = imgs;
    this.selectedImageIndex = startIndex;
    this.selectedItemForModal = item;
    this.showImageModal = true;
  }

  closeImageModal() {
    this.showImageModal = false;
    this.modalImages = [];
    this.selectedItemForModal = null;
    this.selectedImageIndex = 0;
  }

 nextImage() {
  if (this.selectedImageIndex < this.modalImages.length - 1) {
    this.selectedImageIndex++;
  } else {
    this.selectedImageIndex = 0;
  }
}

previousImage() {
  if (this.selectedImageIndex > 0) {
    this.selectedImageIndex--;
  } else {
    this.selectedImageIndex = this.modalImages.length - 1;
  }
}

  selectImage(index: number) {
    this.selectedImageIndex = index;
  }

  getGridCols(): number {
    if (!this.modalImages || this.modalImages.length === 0) return 1;
    if (this.modalImages.length <= 4) return this.modalImages.length;
    if (window.innerWidth < 768) return 3;
    if (window.innerWidth < 1024) return 4;
    return 6;
  }

  // --- AI content generation (generic) ---
  generateFullListingContent() {
    const cardId = this.cardId ?? this.currentCardId;
    if (!cardId) {
      this.aiError = 'No card selected';
      return;
    }

    if (this.activeType !== 'product' && this.activeType !== 'service') {
      this.aiError = 'AI generation is currently supported only for Products and Services.';
      return;
    }

    this.generatingFullContent = true;
    const req: AIContentRequest = {
      cardid: cardId,
      type: this.activeType as AiSupportedType,
      description: `Generate suggestions for ${this.activeType === 'product' ? 'products' : 'services'} that I can offer`,
      language: 'English'
    };

    this.aiContentService.generateContent(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AIContentResponse) => {
          this.generatingFullContent = false;
          if (res.suggestions && Array.isArray(res.suggestions) && res.suggestions.length > 0) {
            this.suggestions = res.suggestions;
            this.showSuggestionsModal = true;
          } else {
            const body = res.content || (res.sections ? res.sections.map(s => `${s.title}\n${s.content}`).join('\n\n') : '');
            if (body) this.listingForm.patchValue({ description: body });
          }
        },
        error: (err) => {
          this.generatingFullContent = false;
          this.aiError = `AI generation failed: ${err?.message || err}`;
        }
      });
  }

toggleExpandItem(itemId: string) {
  if (this.expandedItems.has(itemId)) {
    this.expandedItems.delete(itemId);
  } else {
    this.expandedItems.add(itemId);
  }
}



toggleExpandFeatures(itemId: string) {
  if (this.expandedFeatures.has(itemId)) {
    this.expandedFeatures.delete(itemId);
  } else {
    this.expandedFeatures.add(itemId);
  }
}

toggleExpandImages(itemId: string) {
  if (this.expandedImages.has(itemId)) {
    this.expandedImages.delete(itemId);
  } else {
    this.expandedImages.add(itemId);
  }
}

isItemExpanded(itemId: string): boolean {
  return this.expandedItems.has(itemId);
}

isDescriptionExpanded(itemId: string): boolean {
  return this.expandedDescriptions.has(itemId);
}

isFeaturesExpanded(itemId: string): boolean {
  return this.expandedFeatures.has(itemId);
}

isImagesExpanded(itemId: string): boolean {
  return this.expandedImages.has(itemId);
}
generateListingContentViaAIChat(): void {
  const cardId = this.cardId ?? this.currentCardId;
  if (!cardId) {
    this.aiError = 'No card selected';
    return;
  }

  if (this.activeType !== 'product' && this.activeType !== 'service') {
    this.aiError = 'AI generation is supported only for Products and Services.';
    return;
  }

  // Enhanced return URL construction to preserve exact panel state
  let returnUrl = this.router.url;
  
  // If we're in ViewCard component, include panel info as query params
  if (returnUrl.includes('/view/')) {
    // Build enhanced returnUrl with panel information for ViewCard
    const urlParts = returnUrl.split('?');
    const baseUrl = urlParts[0];
    const existingParams = new URLSearchParams(urlParts[1] || '');
    
    // Add panel information to preserve state
    existingParams.set('panel', this.activeType === 'product' ? 'products' : 'services');
    existingParams.set('openForm', 'true'); // Flag to auto-open create form
    
    returnUrl = `${baseUrl}?${existingParams.toString()}`;
  } else {
    // For standalone listing page, add query params to current URL
    const urlParts = returnUrl.split('?');
    const baseUrl = urlParts[0];
    const existingParams = new URLSearchParams(urlParts[1] || '');
    
    existingParams.set('openForm', 'true'); // Flag to auto-open create form
    existingParams.set('activeType', this.activeType); // Preserve active tab
    
    returnUrl = `${baseUrl}?${existingParams.toString()}`;
  }

  console.log('🔄 Enhanced returnUrl constructed:', returnUrl);

  // Pass the enhanced returnUrl so AI chat can navigate back correctly
  this.router.navigate(['/ai-chat'], {
    queryParams: {
      cardId,
      type: this.activeType,
      returnUrl: returnUrl
    }
  });
}


applyAISuggestionFromChat(suggestion: AISelectionData) {
  console.log('🔄 Applying AI suggestion directly:', suggestion);
  
  const needsTabSwitch = suggestion.type && suggestion.type !== this.activeType;
  
  // First, switch to the correct tab if type is provided and different
  if (needsTabSwitch && (suggestion.type === 'product' || suggestion.type === 'service')) {
    console.log('🔄 Switching to tab:', suggestion.type, '(current:', this.activeType + ')');
    this.selectType(suggestion.type, false); // false = don't fetch data, just switch tab
  }
  
  // Open create form first
  this.skipFormReset = true; // Prevent form reset when opening
  this.skipImageReset = true;
  this.openCreateForm();
  
  // Then populate with AI suggestion data - adjust timeout based on whether we switched tabs
  const populateTimeout = needsTabSwitch ? 150 : 50;
  setTimeout(() => {
    console.log('🔄 Populating form with AI suggestion data:', {
      nameOrTitle: suggestion.name,
      description: suggestion.description,
      price: suggestion.price ?? 0,
      currency: suggestion.currency ?? 'INR'
    });
    
    this.listingForm.patchValue({
      nameOrTitle: suggestion.name,
      description: suggestion.description,
      price: suggestion.price ?? 0,
      currency: suggestion.currency ?? 'INR'
    });
    
    // Handle features array properly with validation
    const featuresArray = this.listingForm.get('features') as FormArray;
    featuresArray.clear(); // Clear existing features
    
    const validFeatures = (suggestion.features || []).filter(f => f && f.trim());
    const featuresToAdd = validFeatures.slice(0, this.maxFeaturesAllowed);
    const excessFeatures = validFeatures.length - this.maxFeaturesAllowed;
    
    featuresToAdd.forEach(feature => {
      console.log('🔄 Adding feature:', feature);
      featuresArray.push(this.fb.control(feature.trim()));
    });
    
    // Show notification if there were excess features
    if (excessFeatures > 0) {
      this.featureErrorMessage = `AI suggested ${validFeatures.length} features, but only ${this.maxFeaturesAllowed} are allowed. ${excessFeatures} features were not added. You can manually add more by removing existing ones first.`;
      console.log('⚠️ Feature limit notification:', this.featureErrorMessage);
      
      // Clear the notification after 8 seconds
      setTimeout(() => {
        this.featureErrorMessage = '';
      }, 8000);

    }
    if (suggestion.images && suggestion.images.length > 0) {
      this.existingImages = [...suggestion.images];
      this.imagePreviews = [...suggestion.images];
      console.log('🔧 Added AI images:', this.existingImages);
    }
    
    console.log('✅ Form populated successfully with AI suggestion');
  }, populateTimeout);
  
  this.selectedSuggestion = suggestion;
}


  applySuggestion(suggestion: { name: string; description: string }) {
  // Open the create form in create mode (not editing existing item)
  this.openCreateForm();

  // Patch values after form is open
  setTimeout(() => {
    this.listingForm.patchValue({
      nameOrTitle: suggestion.name,
      description: suggestion.description
    });
  }, 0);

  // Save the selected suggestion (optional for UX)
  this.selectedSuggestion = suggestion;

  // Close the suggestions modal
  this.showSuggestionsModal = false;
}

}